/**
 * SOLID Review
 * S — Strong. One concern: what the `ClientRoundServer` socket answers while nothing is connected to it.
 * O — Strong. A contract op added later fails the contract-coverage test, not a cast.
 * L — Strong. Implements `IPortClientRoundServer` exactly: every op, the same answer.
 * I — Strong. The contract's ops and nothing else.
 * D — Strong. The kernel's failure builder and the port's interface.
 */
import { portFailure } from '@ml3d/kernel';
import type { IPortClientRoundServer } from './contracts';

/**
 * THE `ClientRoundServer` SOCKET'S NULL OBJECT (frontend-application-oo P6, 2026-10-28; `unavailable()` built it reflectively until
 * then). The frontend's failure policy (strict-ports FE1): a socket the assembly never connected degrades the move that needed
 * it — every op answers `UNAVAILABLE` — and the rest of the app still renders. The kernel guards it on the same terms as a real
 * provider, which is why it answers in the contract's own Result shape. The production-wiring test, not this, keeps an
 * unconnected socket from shipping: it asserts `assertAssembled` with an empty allowlist.
 */
export class UnavailableClientRoundServer implements IPortClientRoundServer {
  private readonly failure = portFailure('UNAVAILABLE', 'ClientRoundServer is not connected.');

  async startRound() {
    return this.failure;
  }

  async rollDice() {
    return this.failure;
  }

  async peekDice() {
    return this.failure;
  }

  async announceClaim() {
    return this.failure;
  }

  async announceBlind() {
    return this.failure;
  }

  async believeClaim() {
    return this.failure;
  }

  async liftCup() {
    return this.failure;
  }

  async getActiveRound() {
    return this.failure;
  }

  async getActiveRoundForRestore() {
    return this.failure;
  }

  async diceRanking() {
    return this.failure;
  }
}
